import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../utils/api';
import { marked } from 'marked';
import { Link } from 'react-router-dom';
import { isGuestAllowed } from '../utils/guestMode';
import { clearAuthSession } from '../utils/authStorage';
import { usePomodoro } from '../pomodoro/PomodoroContext';

type ChecklistItem = {
  id: string;
  title: string;
  guideSlug?: string | null;
  parentId?: string | null; // 支持子任务：null/undefined 表示根任务
  order: number;
  createdAt: number;
  updatedAt: number;
};

type DayState = {
  dayKey: string; // yyyy-mm-dd (local, with 4:00 cutoff)
  doneIds: string[];
  updatedAt: number;
};

type TodayDataV1 = {
  version: 1;
  items: ChecklistItem[];
  days: Record<string, DayState>;
};

type TaskGuide = {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  content: string;
  updatedAt: string;
};

type AuthUser = {
  id: number;
  username: string;
  email: string;
  role: string;
};

type TodayCloudResponse = {
  data: TodayDataV1 | null;
  updatedAt: string | null;
};

const STORAGE_KEY = 'personal_blog.today.v1'; // legacy（旧单 key）
const STORAGE_KEY_PREFIX = 'personal_blog.today.v1';
const GUIDE_CACHE_KEY_PREFIX = 'personal_blog.today.guide_cache.v1'; // 后面会拼 userId/guest，避免不同用户/游客互串
const DAY_CUTOFF_HOUR = 4;
const KEEP_DAYS = 90;
const LOCAL_GUIDES_KEY = 'personal_blog.guides.local.v1';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function formatMMSS(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function formatLocalDayKey(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseDayKey(dayKey: string) {
  const [y, m, d] = dayKey.split('-').map((x) => Number(x));
  return new Date(y, (m ?? 1) - 1, d ?? 1, 0, 0, 0, 0);
}

function getEffectiveDayKey(now = new Date(), cutoffHour = DAY_CUTOFF_HOUR) {
  const d = new Date(now);
  if (d.getHours() < cutoffHour) d.setDate(d.getDate() - 1);
  return formatLocalDayKey(d);
}

function safeJsonParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function todayStorageKeyFor(userId?: number | null) {
  return userId ? `${STORAGE_KEY_PREFIX}.user.${userId}` : `${STORAGE_KEY_PREFIX}.guest`;
}

function migrateLegacyTodayKeyIfNeeded(targetKey: string) {
  if (!targetKey || targetKey === STORAGE_KEY) return;
  const legacy = localStorage.getItem(STORAGE_KEY);
  if (!legacy) return;
  // 仅当目标 key 为空时迁移，避免覆盖
  if (!localStorage.getItem(targetKey)) {
    localStorage.setItem(targetKey, legacy);
  }
  localStorage.removeItem(STORAGE_KEY);
}

function initialTodayStorageKey() {
  const token = localStorage.getItem('today_token');
  const u = safeJsonParse<any>(localStorage.getItem('today_user'));
  const userId = token && u?.id ? Number(u.id) : null;
  const key = todayStorageKeyFor(userId);
  migrateLegacyTodayKeyIfNeeded(key);
  return key;
}

function loadTodayData(storageKey: string): TodayDataV1 {
  const raw = localStorage.getItem(storageKey);
  const parsed = safeJsonParse<any>(raw);

  // v1（当前）
  if (
    parsed &&
    parsed.version === 1 &&
    Array.isArray(parsed.items) &&
    parsed.days &&
    typeof parsed.days === 'object'
  ) {
    return parsed as TodayDataV1;
  }

  // 兼容旧结构（无 version 字段）：避免更新代码后直接把旧数据“当成无效并覆盖为空”
  if (parsed && !('version' in parsed) && Array.isArray(parsed.items) && parsed.days && typeof parsed.days === 'object') {
    return { version: 1, items: parsed.items as ChecklistItem[], days: parsed.days as Record<string, DayState> };
  }

  // 兜底：如果本地有内容但解析/结构不对，先备份一份，避免“不可逆丢数据”
  if (raw) {
    try {
      localStorage.setItem(`${storageKey}.backup.${Date.now()}`, raw);
    } catch {
      // ignore
    }
  }

  return { version: 1, items: [], days: {} };
}

function loadGuideCache(): Record<string, TaskGuide> {
  // 兼容旧 key（未分用户）：尽量读出来，避免升级后体验倒退
  const parsed = safeJsonParse<Record<string, TaskGuide>>(localStorage.getItem('personal_blog.today.guide_cache.v1'));
  if (parsed && typeof parsed === 'object') return parsed;
  if (!parsed || typeof parsed !== 'object') return {};
  return parsed;
}

function saveGuideCache(key: string, map: Map<string, TaskGuide>, maxEntries = 300) {
  // 控制体积：只保留最近的 maxEntries 个（按插入顺序）
  const entries = Array.from(map.entries());
  const sliced = entries.length > maxEntries ? entries.slice(entries.length - maxEntries) : entries;
  const obj: Record<string, TaskGuide> = {};
  for (const [slug, guide] of sliced) obj[slug] = guide;
  localStorage.setItem(key, JSON.stringify(obj));
}

function saveTodayData(data: TodayDataV1, storageKey: string) {
  localStorage.setItem(storageKey, JSON.stringify(data));
}

function ensureDay(data: TodayDataV1, dayKey: string) {
  if (!data.days[dayKey]) {
    data.days[dayKey] = {
      dayKey,
      doneIds: [],
      updatedAt: Date.now(),
    };
  }
}

function purgeOldDays(data: TodayDataV1, currentDayKey: string, keepDays = KEEP_DAYS) {
  const current = parseDayKey(currentDayKey).getTime();
  const cutoff = current - (keepDays - 1) * 24 * 60 * 60 * 1000;
  for (const k of Object.keys(data.days)) {
    const t = parseDayKey(k).getTime();
    if (Number.isFinite(t) && t < cutoff) {
      delete data.days[k];
    }
  }
}

function uuid() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeGuideSlug(input: string) {
  let s = input.trim();
  // 允许粘贴完整路径：/guides/xxx
  if (s.startsWith('/guides/')) s = s.slice('/guides/'.length);
  // 去掉前后斜杠
  s = s.replace(/^\/+/, '').replace(/\/+$/, '');
  // 去掉 query/hash
  s = s.split('?')[0]?.split('#')[0] ?? '';
  return s.trim();
}

function slugify(s: string) {
  return s
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function isValidGuideSlug(slug: string) {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

function suggestSlugSmart(input: string) {
  const raw = normalizeGuideSlug(input);
  if (!raw) return '';
  if (isValidGuideSlug(raw)) return raw;

  // 简单中文词典（可随业务扩展）：解决常见“中文->英文 slug”场景
  const dict: Array<[RegExp, string]> = [
    [/游戏/g, 'game'],
    [/调研|研究/g, 'research'],
    [/阅读/g, 'reading'],
    [/写作/g, 'writing'],
    [/编码|编程|代码/g, 'coding'],
    [/健身|训练/g, 'fitness'],
    [/学习/g, 'study'],
    [/复盘/g, 'review'],
    [/任务/g, 'task'],
  ];
  let s = raw;
  for (const [re, rep] of dict) s = s.replace(re, ` ${rep} `);

  return slugify(s);
}

function cloneData<T>(v: T): T {
  // structuredClone 在较老浏览器/部分环境可能不存在；用 JSON 兜底（本页数据结构无函数/Date）。
  if (typeof globalThis !== 'undefined' && 'structuredClone' in globalThis) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (globalThis as any).structuredClone(v) as T;
  }
  return JSON.parse(JSON.stringify(v)) as T;
}

function hasMeaningfulLocalData(d: TodayDataV1) {
  // days 一定会有 todayKey（ensureDay），所以不能用 days 是否为空判断
  const hasItems = d.items.length > 0;
  const hasAnyDone = Object.values(d.days ?? {}).some((s) => (s?.doneIds?.length ?? 0) > 0);
  return hasItems || hasAnyDone;
}

marked.setOptions({ gfm: true, breaks: true });

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const SubTitle = styled.div`
  margin-top: 0.25rem;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.95rem;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Pill = styled.div`
  padding: 0.45rem 0.7rem;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
`;

const Button = styled.button`
  padding: 0.55rem 0.85rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${({ theme }) => theme.shadow};
    transform: translateY(-1px);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 1.25rem;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  box-shadow: ${({ theme }) => theme.shadow};
`;

const Left = styled(Card)`
  padding: 1rem;
`;

const Right = styled(Card)`
  padding: 1.25rem 1.25rem 1.5rem;
  min-height: 420px;
`;

const Row = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
  outline: none;
  font-size: 0.95rem;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const SmallInput = styled(Input)`
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
`;

const List = styled.div`
  margin-top: 1rem;
`;

const ItemRow = styled.div<{ $active?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.7rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.border : 'transparent')};
  background: ${({ theme, $active }) => ($active ? theme.surfaceAlt : 'transparent')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  min-width: 18px;
  min-height: 18px;
`;

const LeftControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex: 0 0 auto;
`;

const ExpandCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
`;

const InlineHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
`;

const InlineMeta = styled.span`
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemTitle = styled.div<{ $done?: boolean }>`
  color: ${({ theme }) => theme.text};
  font-weight: 600;
  text-decoration: ${({ $done }) => ($done ? 'line-through' : 'none')};
  opacity: ${({ $done }) => ($done ? 0.65 : 1)};
  min-width: 0;
`;

const ItemMeta = styled.div`
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.textSecondary};
`;

const ItemContent = styled.div`
  min-width: 0;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 8px;
  padding: 0.35rem 0.5rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  padding: 1.25rem 0.25rem;
`;

const Toast = styled.div`
  position: fixed;
  top: 5rem;
  right: 1rem;
  z-index: 1500;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 12px;
  padding: 0.65rem 0.8rem;
  box-shadow: ${({ theme }) => theme.hoverShadow};
  max-width: min(520px, 92vw);
`;

const PomodoroCard = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  border-radius: 12px;
  padding: 0.85rem 0.9rem;
  margin: 0.75rem 0 1rem;
`;

const PomodoroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const PomodoroTime = styled.div`
  font-size: 2.1rem;
  font-weight: 900;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.text};
`;

const PomodoroMeta = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
  margin-top: 0.15rem;
`;

const PomodoroActions = styled.div`
  display: flex;
  gap: 0.45rem;
  align-items: center;
  flex-wrap: wrap;
`;

const TinyBtn = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.primary : theme.border)};
  background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surface)};
  color: ${({ theme, $primary }) => ($primary ? theme.footerText : theme.text)};
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surfaceAlt)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FocusOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: white;
  overflow: hidden;

  background: radial-gradient(circle at 30% 20%, rgba(77, 171, 247, 0.25), transparent 45%),
    radial-gradient(circle at 70% 80%, rgba(120, 94, 240, 0.22), transparent 50%),
    linear-gradient(120deg, #0b1020, #0f1216 55%, #0b0e12);

  &::before {
    content: "";
    position: absolute;
    inset: -40%;
    background: conic-gradient(from 180deg, rgba(77, 171, 247, 0.12), rgba(120, 94, 240, 0.10), rgba(77, 171, 247, 0.12));
    animation: spin 14s linear infinite;
    filter: blur(18px);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FocusContent = styled.div`
  position: relative;
  width: min(920px, 100%);
  text-align: center;
  transform: translateY(-5vh);
`;

const FocusTitle = styled.div`
  font-size: clamp(1.25rem, 2.8vw, 1.7rem);
  opacity: 0.9;
  margin-bottom: 0.75rem;
  font-weight: 700;
`;

const FocusBigTime = styled.div`
  font-size: clamp(5.2rem, 13vw, 7.4rem);
  font-weight: 900;
  letter-spacing: 1px;
`;

const FocusGuideWrap = styled.div`
  margin: 2.2rem auto 0;
  width: min(760px, 100%);
  text-align: center;
`;

const FocusGuideTitle = styled.div`
  font-weight: 800;
  opacity: 0.78;
  margin-bottom: 0.45rem;
`;

const FocusGuideBody = styled.div`
  opacity: 0.68;
  font-size: 1rem;
  line-height: 1.85;
  max-height: 30vh;
  overflow: auto;

  h1, h2, h3 { font-size: 1rem; margin: 0.6rem 0 0.35rem; }
  p { margin: 0.45rem 0; }
  /* 专注屏：去掉列表圆点/编号，让内容更“原则卡片”风格 */
  ul, ol { margin: 0.55rem 0; padding-left: 0; list-style: none; }
  li { margin: 0.55rem 0; }
  code { opacity: 0.85; }
`;

const FocusButtons = styled.div`
  margin-top: 1.25rem;
  display: flex;
  justify-content: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 50;
`;

const ModalCard = styled.div`
  width: min(520px, 100%);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  color: ${({ theme }) => theme.text};
  border-radius: 12px;
  padding: 1rem;
  box-shadow: ${({ theme }) => theme.hoverShadow};
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.primary : theme.border)};
  background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surface)};
  color: ${({ theme, $primary }) => ($primary ? theme.footerText : theme.text)};
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surfaceAlt)};
  }
`;

const Markdown = styled.div`
  color: ${({ theme }) => theme.text};
  line-height: 1.8;

  h1, h2, h3 {
    margin: 1.2rem 0 0.75rem;
  }
  h1 { font-size: 2rem; }
  h2 { font-size: 1.5rem; }
  h3 { font-size: 1.2rem; }

  p { margin: 0.8rem 0; }

  ul, ol {
    padding-left: 1.4rem;
    margin: 0.8rem 0;
  }
  li { margin: 0.3rem 0; }

  code {
    background: ${({ theme }) => theme.mutedBg};
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.92em;
  }

  pre {
    background: #0b1020;
    color: #e6e6e6;
    padding: 1rem;
    border-radius: 12px;
    overflow: auto;
    margin: 1rem 0;
  }

  blockquote {
    margin: 1rem 0;
    padding: 0.6rem 0.9rem;
    border-left: 4px solid ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.surfaceAlt};
    color: ${({ theme }) => theme.textSecondary};
    border-radius: 8px;
  }
`;

const RightTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
`;

const RightTitle = styled.div`
  font-weight: 800;
  color: ${({ theme }) => theme.text};
`;

const RightHint = styled.div`
  color: ${({ theme }) => theme.textSecondary};
`;

const RightLink = styled(Link)`
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

const ModalField = styled.label`
  display: grid;
  gap: 0.35rem;
  margin-top: 0.75rem;
  color: ${({ theme }) => theme.text};
`;

const ModalInput = styled.input`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.12);
  }
`;

const Banner = styled.div`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  margin-bottom: 0.75rem;
`;

const InlineAuth = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const AuthName = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
`;

const SlugHintRow = styled.div`
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.85rem;
`;

const SlugHintBtn = styled.button`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 8px;
  padding: 0.2rem 0.45rem;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }
`;

const Today: React.FC = () => {
  const [authTick, setAuthTick] = useState(0);
  const refreshAuth = () => setAuthTick((x) => x + 1);
  const pomodoro = usePomodoro();

  // 同 tab 的登录态变化（Layout 退出/登录）不会触发 storage 事件；用自定义事件兜底
  useEffect(() => {
    const handler = () => refreshAuth();
    window.addEventListener('today-auth-changed', handler as any);
    window.addEventListener('storage', handler as any);
    return () => {
      window.removeEventListener('today-auth-changed', handler as any);
      window.removeEventListener('storage', handler as any);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dayKey, setDayKey] = useState(() => getEffectiveDayKey());
  const [data, setData] = useState<TodayDataV1>(() => {
    const key = initialTodayStorageKey();
    const d = loadTodayData(key);
    ensureDay(d, getEffectiveDayKey());
    purgeOldDays(d, getEffectiveDayKey(), KEEP_DAYS);
    saveTodayData(d, key);
    return d;
  });

  const [selectedId, setSelectedId] = useState<string | null>(() => (pomodoro.boundTask?.id ? pomodoro.boundTask.id : null));
  const [newTitle, setNewTitle] = useState('');

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editGuideSlug, setEditGuideSlug] = useState('');
  const [editSlugErr, setEditSlugErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [confirmParentId, setConfirmParentId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);
  const missingGuideNotifiedRef = useRef<Set<string>>(new Set());

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const guideCacheRef = useRef<Map<string, TaskGuide>>(new Map());
  const persistGuideCacheTimerRef = useRef<number | null>(null);
  const prefetchedSlugsRef = useRef<Set<string>>(new Set());

  // 预取全部原则（用于输入提示 + warm cache，避免切换任务时反复 loading）
  const [allGuides, setAllGuides] = useState<TaskGuide[]>([]);

  const isLoggedIn = useMemo(() => !!localStorage.getItem('today_token'), [authTick]);
  const guestOk = isGuestAllowed();

  // Auth (for cloud sync)
  const authUser = useMemo(() => {
    const raw = safeJsonParse<AuthUser>(localStorage.getItem('today_user'));
    return raw ?? null;
  }, [authTick]);

  const todayStorageKey = useMemo(() => {
    const userId = isLoggedIn && authUser?.id ? authUser.id : null;
    const key = todayStorageKeyFor(userId);
    migrateLegacyTodayKeyIfNeeded(key);
    return key;
  }, [isLoggedIn, authUser?.id]);

  const todayStorageKeyRef = useRef(todayStorageKey);
  useEffect(() => {
    todayStorageKeyRef.current = todayStorageKey;
    const d = loadTodayData(todayStorageKey);
    ensureDay(d, getEffectiveDayKey());
    purgeOldDays(d, getEffectiveDayKey(), KEEP_DAYS);
    saveTodayData(d, todayStorageKey);
    setData(d);
    setDayKey(getEffectiveDayKey());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayStorageKey]);

  // Cloud sync
  const [cloudUpdatedAt, setCloudUpdatedAt] = useState<string | null>(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudErr, setCloudErr] = useState<string | null>(null);
  const [syncConflict, setSyncConflict] = useState<{ serverUpdatedAt: string } | null>(null);
  const syncTimerRef = useRef<number | null>(null);

  const logout = () => {
    clearAuthSession();
    setCloudUpdatedAt(null);
    setSyncConflict(null);
    refreshAuth();
    window.dispatchEvent(new Event('today-auth-changed'));
  };

  const pullCloud = async () => {
    setCloudLoading(true);
    setCloudErr(null);
    try {
      const res = await api.get<TodayCloudResponse>('/today-data');
      const cloud = res.data;
      setCloudUpdatedAt(cloud.updatedAt);
      if (cloud.data) {
        saveTodayData(cloud.data, todayStorageKeyRef.current);
        setData(cloud.data);
        setDayKey(getEffectiveDayKey());
      }
      setSyncConflict(null);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setCloudErr('服务器尚未部署云端接口（/api/today-data）。请更新服务器后再试。');
      } else {
        setCloudErr('拉取云端数据失败。');
      }
    } finally {
      setCloudLoading(false);
    }
  };

  const pushCloud = async (force = false) => {
    setCloudLoading(true);
    setCloudErr(null);
    try {
      const baseUpdatedAt = force ? undefined : cloudUpdatedAt ?? undefined;
      const res = await api.put<{ updatedAt: string }>('/today-data', { data, baseUpdatedAt });
      setCloudUpdatedAt(res.data.updatedAt);
      setSyncConflict(null);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setCloudErr('服务器尚未部署云端接口（/api/today-data）。请更新服务器后再试。');
        return;
      }
      if (e?.response?.status === 409) {
        const serverUpdatedAt = e?.response?.data?.updatedAt as string | undefined;
        if (serverUpdatedAt) setSyncConflict({ serverUpdatedAt });
        setCloudErr('检测到多设备冲突：云端已更新。请先拉取，再决定是否覆盖。');
      } else if (e?.response?.status === 401) {
        clearAuthSession();
        refreshAuth();
        window.dispatchEvent(new Event('today-auth-changed'));
        setCloudUpdatedAt(null);
        setCloudErr('未登录或登录已过期。');
      } else {
        setCloudErr('同步到云端失败。');
      }
    } finally {
      setCloudLoading(false);
    }
  };

  const schedulePersistGuideCache = () => {
    if (persistGuideCacheTimerRef.current) window.clearTimeout(persistGuideCacheTimerRef.current);
    persistGuideCacheTimerRef.current = window.setTimeout(() => {
      try {
        const userKey = `${GUIDE_CACHE_KEY_PREFIX}.${authUser?.id ?? 'guest'}`;
        saveGuideCache(userKey, guideCacheRef.current);
      } catch {
        // ignore（localStorage 可能满）
      }
    }, 200);
  };

  const cachePut = (slug: string, g: TaskGuide) => {
    if (!slug) return;
    guideCacheRef.current.set(slug, g);
    schedulePersistGuideCache();
  };

  // 清理“只剩用户信息但 token 不存在”的脏状态（否则会出现 UI/缓存错配）
  useEffect(() => {
    if (isLoggedIn) return;
    const hasUser = !!localStorage.getItem('today_user');
    if (hasUser) {
      clearAuthSession();
      refreshAuth();
      window.dispatchEvent(new Event('today-auth-changed'));
    }
  }, [isLoggedIn]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  // 先把持久缓存灌进内存缓存（避免切换页面回来又 loading）
  useEffect(() => {
    try {
      // 登录态/用户变化时，清空内存缓存与预取标记，避免“上一用户/游客”的缓存污染
      guideCacheRef.current = new Map();
      prefetchedSlugsRef.current = new Set();

      const userKey = `${GUIDE_CACHE_KEY_PREFIX}.${authUser?.id ?? 'guest'}`;
      // 严格隔离：仅从 userKey 读取；不再回退读旧全局缓存（避免登录云端为空但 Today 仍显示旧缓存原则）
      const stored = safeJsonParse<Record<string, TaskGuide>>(localStorage.getItem(userKey)) ?? {};
      for (const [slug, g] of Object.entries(stored)) {
        if (slug && g) guideCacheRef.current.set(slug, g);
      }
    } catch {
      // ignore
    }
    return () => {
      if (persistGuideCacheTimerRef.current) window.clearTimeout(persistGuideCacheTimerRef.current);
    };
  }, [authUser?.id]);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        // 系统内置原则：所有用户可读
        const sysRes = await api.get<TaskGuide[]>('/task-guides');
        const sysList = sysRes.data ?? [];

        // 用户自建原则：登录走云端，游客走本地
        let myList: TaskGuide[] = [];
        if (isLoggedIn) {
          const res = await api.get<TaskGuide[]>('/user-guides');
          myList = res.data ?? [];
        } else {
          myList = safeJsonParse<TaskGuide[]>(localStorage.getItem(LOCAL_GUIDES_KEY)) ?? [];
        }

        if (cancelled) return;
        const map = new Map<string, TaskGuide>();
        for (const g of [...sysList, ...myList]) {
          if (g?.slug) map.set(g.slug, g);
        }
        const merged = Array.from(map.values());
        setAllGuides(merged);
        for (const g of merged) if (g?.slug) cachePut(g.slug, g);
      } catch {
        // ignore：这是体验增强，不应阻塞 Today 页面
      }
    };
    void loadAll();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  // 一次性预取：把“清单里引用到的所有 slug”的原则内容都提前塞进缓存
  // 这样第一次切换/第一次选中也尽量不出现 loading。
  useEffect(() => {
    const slugs = Array.from(
      new Set(
        data.items
          .map((it) => normalizeGuideSlug(it.guideSlug ?? ''))
          .filter((s) => !!s),
      ),
    );
    if (!slugs.length) return;

    const missing = slugs.filter((s) => !guideCacheRef.current.has(s) && !prefetchedSlugsRef.current.has(s));
    if (!missing.length) return;

    // 标记为已尝试预取（避免无限重试/循环触发）
    for (const s of missing) prefetchedSlugsRef.current.add(s);

    let cancelled = false;

    const fetchOne = async (slug: string) => {
      // 如果 allGuides 里已经有完整内容，直接用它
      const fromList = allGuides.find((g) => g.slug === slug);
      if (fromList && fromList.content) {
        cachePut(slug, fromList);
        return;
      }

      try {
        // 系统内置：走 task-guides
        if (slug.startsWith('sys-')) {
          const res = await api.get<TaskGuide>(`/task-guides/slug/${encodeURIComponent(slug)}`);
          if (cancelled) return;
          cachePut(slug, res.data);
          return;
        }
        if (isLoggedIn) {
          const res = await api.get<TaskGuide>(`/user-guides/slug/${encodeURIComponent(slug)}`);
          if (cancelled) return;
          cachePut(slug, res.data);
          return;
        }
        // guest local：从本地列表找
        const local = safeJsonParse<TaskGuide[]>(localStorage.getItem(LOCAL_GUIDES_KEY)) ?? [];
        const found = local.find((g) => g.slug === slug) ?? null;
        if (found) cachePut(slug, found);
      } catch (e: any) {
        const status = e?.response?.status;
        if (!slug.startsWith('sys-') && (status === 404 || status === 405)) {
          try {
            if (isLoggedIn) {
              const list = await api.get<TaskGuide[]>('/user-guides', { params: { q: slug } });
              if (cancelled) return;
              const exact = list.data.find((g) => g.slug === slug) ?? list.data[0] ?? null;
              if (exact) cachePut(slug, exact);
            }
          } catch {
            // ignore
          }
        }
      }
    };

    const runWithLimit = async (arr: string[], limit = 4) => {
      const queue = [...arr];
      const workers = Array.from({ length: Math.min(limit, queue.length) }, async () => {
        while (queue.length && !cancelled) {
          const s = queue.shift();
          if (!s) break;
          await fetchOne(s);
        }
      });
      await Promise.all(workers);
    };

    void runWithLimit(missing, 4);

    return () => {
      cancelled = true;
    };
  }, [data.items, allGuides, isLoggedIn]);

  const items = useMemo(() => {
    return [...data.items].sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
  }, [data.items]);

  const todayState = data.days[dayKey] ?? { dayKey, doneIds: [], updatedAt: Date.now() };
  const doneSet = useMemo(() => new Set(todayState.doneIds), [todayState.doneIds]);

  const childrenMap = useMemo(() => {
    const m = new Map<string | null, ChecklistItem[]>();
    for (const it of items) {
      const pid = (it.parentId ?? null) as string | null;
      const arr = m.get(pid) ?? [];
      arr.push(it);
      m.set(pid, arr);
    }
    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
      m.set(k, arr);
    }
    return m;
  }, [items]);

  const getChildren = (id: string) => childrenMap.get(id) ?? [];
  const hasChildren = (id: string) => getChildren(id).length > 0;

  const isDerivedDone = useMemo(() => {
    const memo = new Map<string, boolean>();
    const dfs = (id: string): boolean => {
      if (memo.has(id)) return memo.get(id)!;
      const selfDone = doneSet.has(id);
      const children = getChildren(id);
      if (!children.length) {
        memo.set(id, selfDone);
        return selfDone;
      }
      const allChildrenDone = children.every((c) => dfs(c.id));
      // 规则：父任务“视为完成”= 自己被勾选（可强制） OR 所有子任务完成
      const v = selfDone || allChildrenDone;
      memo.set(id, v);
      return v;
    };

    const fn = (it: ChecklistItem) => dfs(it.id);
    return fn;
  }, [doneSet, childrenMap]);

  const completionText = useMemo(() => {
    const total = items.length;
    const done = items.filter((it) => isDerivedDone(it)).length;
    const pct = total ? Math.round((done / total) * 100) : 0;
    return `${done}/${total}（${pct}%）`;
  }, [items, isDerivedDone]);

  useEffect(() => {
    const tick = window.setInterval(() => {
      const next = getEffectiveDayKey();
      setDayKey((prev) => (prev === next ? prev : next));
    }, 15 * 1000);
    return () => window.clearInterval(tick);
  }, []);

  useEffect(() => {
    // ensure day exists whenever dayKey changes
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      ensureDay(next, dayKey);
      purgeOldDays(next, dayKey, KEEP_DAYS);
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
  }, [dayKey]);

  // 登录后：首次拉取云端；并根据情况做“安全初始化”
  useEffect(() => {
    if (!authUser) return;
    let cancelled = false;
    const init = async () => {
      setCloudLoading(true);
      setCloudErr(null);
      try {
        const res = await api.get<TodayCloudResponse>('/today-data');
        if (cancelled) return;
        setCloudUpdatedAt(res.data.updatedAt);

        const localHas = hasMeaningfulLocalData(data);
        const cloudHas = !!res.data.data && hasMeaningfulLocalData(res.data.data);

        // 云端有/本地空：用云端覆盖本地
        if (cloudHas && !localHas && res.data.data) {
          saveTodayData(res.data.data, todayStorageKeyRef.current);
          setData(res.data.data);
          setDayKey(getEffectiveDayKey());
          return;
        }

        // 云端空/本地有：首次推送本地到云端
        if (!cloudHas && localHas) {
          await api.put('/today-data', { data });
          const again = await api.get<TodayCloudResponse>('/today-data');
          if (cancelled) return;
          setCloudUpdatedAt(again.data.updatedAt);
          return;
        }

        // 两边都有：不自动覆盖，交给你手动点“拉取/覆盖”
      } catch {
        if (cancelled) return;
        setCloudErr('云端初始化失败（可能未登录/网络问题）。');
      } finally {
        if (cancelled) return;
        setCloudLoading(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // 已登录时：本地 data 变化 -> 轻量节流自动同步（遇到冲突则暂停自动同步）
  useEffect(() => {
    if (!authUser) return;
    if (syncConflict) return;
    if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    syncTimerRef.current = window.setTimeout(() => {
      void pushCloud(false);
    }, 900);
    return () => {
      if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, authUser?.id, syncConflict?.serverUpdatedAt]);

  useEffect(() => {
    if (selectedId) return;
    if (!items.length) return;
    const preferred = pomodoro.boundTask?.id ?? null;
    if (preferred && items.some((it) => it.id === preferred)) {
      setSelectedId(preferred);
      return;
    }
    setSelectedId(items[0].id);
  }, [items, selectedId, pomodoro.boundTask?.id]);

  // 番茄运行/暂停中：确保 Today 的选中项跟随当前绑定任务，避免“切页面回来自动选第一条 -> 触发切换任务停止”
  useEffect(() => {
    if (!(pomodoro.running || pomodoro.sessionActive)) return;
    const preferred = pomodoro.boundTask?.id ?? null;
    if (!preferred) return;
    if (!items.some((it) => it.id === preferred)) return;
    if (selectedId === preferred) return;
    setSelectedId(preferred);
  }, [pomodoro.running, pomodoro.sessionActive, pomodoro.boundTask?.id, items, selectedId]);

  const selectedItem = useMemo(() => items.find((it) => it.id === selectedId) ?? null, [items, selectedId]);

  // 绑定番茄到当前选中的任务/子任务；切换任务会在全局番茄里直接停止并重置为专注
  useEffect(() => {
    pomodoro.bindToTask(selectedItem ? { id: selectedItem.id, title: selectedItem.title, guideSlug: selectedItem.guideSlug } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem?.id, selectedItem?.title, selectedItem?.guideSlug]);

  const toggleDone = (id: string) => {
    const it = items.find((x) => x.id === id);
    if (!it) return;

    // 父任务：若存在未完成子任务，并且当前要“勾选为完成”，则弹确认
    if (hasChildren(id)) {
      const currentlyDone = doneSet.has(id); // 只看显式勾选，不看派生
      const allChildrenDone = getChildren(id).every((c) => isDerivedDone(c));
      const wantSetDone = !currentlyDone;
      if (wantSetDone && !allChildrenDone) {
        setConfirmParentId(id);
        return;
      }
    }

    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      ensureDay(next, dayKey);
      const s = next.days[dayKey];
      const exists = s.doneIds.includes(id);
      s.doneIds = exists ? s.doneIds.filter((x) => x !== id) : [...s.doneIds, id];
      s.updatedAt = Date.now();
      next.days[dayKey] = s;
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
  };

  const confirmMarkParentDone = () => {
    const id = confirmParentId;
    if (!id) return;
    setConfirmParentId(null);
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      ensureDay(next, dayKey);
      const s = next.days[dayKey];
      // 强制完成：主任务 + 其所有后代一起标记完成
      const descendants = collectDescendants(id);
      const toMark = new Set([id, ...descendants]);
      const cur = new Set(s.doneIds);
      for (const tid of toMark) cur.add(tid);
      s.doneIds = Array.from(cur);
      s.updatedAt = Date.now();
      next.days[dayKey] = s;
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
  };

  const addItem = () => {
    const title = newTitle.trim() || '新任务';
    const id = uuid();
    const now = Date.now();
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      const maxOrder = next.items.filter((it) => (it.parentId ?? null) === null).reduce((m, it) => Math.max(m, it.order), 0);
      const item: ChecklistItem = {
        id,
        title,
        guideSlug: null,
        parentId: null,
        order: maxOrder + 10,
        createdAt: now,
        updatedAt: now,
      };
      next.items.push(item);
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
    setNewTitle('');
    // 统一交互：新增后直接进入编辑态（标题默认选中，可直接替换；也可直接绑定原则）
    setSelectedId(id);
    setEditingId(id);
    setEditTitle(title);
    setEditGuideSlug('');
    setEditSlugErr(null);
  };

  const addChild = (parentId: string) => {
    const id = uuid();
    const now = Date.now();
    setExpanded((prev) => ({ ...prev, [parentId]: true }));
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      const siblings = next.items.filter((it) => (it.parentId ?? null) === parentId);
      const maxOrder = siblings.reduce((m, it) => Math.max(m, it.order), 0);
      const item: ChecklistItem = {
        id,
        title: '新子任务',
        guideSlug: null,
        parentId,
        order: maxOrder + 10,
        createdAt: now,
        updatedAt: now,
      };
      next.items.push(item);
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
    // 统一交互：新增子任务后直接进入编辑态（标题默认选中）
    setSelectedId(id);
    setEditingId(id);
    setEditTitle('新子任务');
    setEditGuideSlug('');
    setEditSlugErr(null);
  };

  const collectDescendants = (rootId: string) => {
    const out: string[] = [];
    const stack = [rootId];
    while (stack.length) {
      const cur = stack.pop()!;
      const cs = getChildren(cur);
      for (const c of cs) {
        out.push(c.id);
        stack.push(c.id);
      }
    }
    return out;
  };

  const removeItem = (id: string) => {
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      const descendants = collectDescendants(id);
      const toRemove = new Set([id, ...descendants]);
      next.items = next.items.filter((it) => !toRemove.has(it.id));
      for (const k of Object.keys(next.days)) {
        next.days[k].doneIds = next.days[k].doneIds.filter((x) => !toRemove.has(x));
      }
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
    if (selectedId === id) setSelectedId(null);
    if (editingId === id) setEditingId(null);
  };

  const moveItem = (id: string, dir: 'up' | 'down') => {
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      const cur = next.items.find((x) => x.id === id);
      if (!cur) return prev;
      const pid = (cur.parentId ?? null) as string | null;
      const siblings = next.items
        .filter((it) => (it.parentId ?? null) === pid)
        .sort((a, b) => a.order - b.order || a.createdAt - b.createdAt);
      const idx = siblings.findIndex((x) => x.id === id);
      if (idx < 0) return prev;
      const swapWith = dir === 'up' ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= siblings.length) return prev;
      const a = siblings[idx];
      const b = siblings[swapWith];
      const ao = a.order;
      a.order = b.order;
      b.order = ao;
      a.updatedAt = Date.now();
      b.updatedAt = Date.now();
      // write back
      const map = new Map(siblings.map((it) => [it.id, it]));
      next.items = next.items.map((it) => map.get(it.id) ?? it);
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
  };

  const startEdit = (it: ChecklistItem) => {
    setEditingId(it.id);
    setEditTitle(it.title);
    setEditGuideSlug(it.guideSlug ?? '');
    setEditSlugErr(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditGuideSlug('');
    setEditSlugErr(null);
  };

  const saveEdit = () => {
    const title = editTitle.trim();
    const raw = normalizeGuideSlug(editGuideSlug);
    const suggested = suggestSlugSmart(raw);
    const slug = isValidGuideSlug(raw) ? raw : suggested;
    if (!editingId) return;
    if (!title) return;

    // 用户填了 slug，但无法转成合法 slug：阻止保存并提示
    if (raw && !slug) {
      setEditSlugErr('guideSlug 仅支持小写字母/数字/-。可以点击“应用建议”自动生成。');
      return;
    }

    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      next.items = next.items.map((it) => {
        if (it.id !== editingId) return it;
        return {
          ...it,
          title,
          guideSlug: slug || null,
          updatedAt: Date.now(),
        };
      });
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
    setEditingId(null);
  };

  const resetDone = () => {
    setData((prev) => {
      const next = cloneData(prev) as TodayDataV1;
      ensureDay(next, dayKey);
      next.days[dayKey].doneIds = [];
      next.days[dayKey].updatedAt = Date.now();
      saveTodayData(next, todayStorageKeyRef.current);
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !(prev[id] ?? true) }));
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `today-data-${dayKey}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importJson = async (file: File) => {
    const text = await file.text();
    const parsed = safeJsonParse<TodayDataV1>(text);
    if (!parsed || parsed.version !== 1) {
      alert('导入失败：文件格式不正确（version !== 1）。');
      return;
    }
    ensureDay(parsed, getEffectiveDayKey());
    purgeOldDays(parsed, getEffectiveDayKey(), KEEP_DAYS);
    saveTodayData(parsed, todayStorageKeyRef.current);
    setData(parsed);
    setDayKey(getEffectiveDayKey());
  };

  // Right panel: guide preview
  const [guideLoading, setGuideLoading] = useState(false);
  const [guide, setGuide] = useState<TaskGuide | null>(null);
  const [guideErr, setGuideErr] = useState<string | null>(null);

  // Pomodoro（全局状态）
  const focusOverlayRef = useRef<HTMLDivElement | null>(null);

  const enterFullscreen = async () => {
    try {
      if (document.fullscreenElement) return;
      // 优先对整个页面全屏（更稳定）；遮罩层会盖住导航栏
      await document.documentElement.requestFullscreen?.();
    } catch {
      // ignore
    }
  };

  const exitFullscreen = async () => {
    try {
      if (!document.fullscreenElement) return;
      await document.exitFullscreen?.();
    } catch {
      // ignore
    }
  };

  // 这里用 useLayoutEffect：切换任务时若命中缓存，可以在“绘制前”把内容塞回去，避免先闪一下 loading。
  useLayoutEffect(() => {
    const raw = selectedItem?.guideSlug ?? '';
    const slug = normalizeGuideSlug(raw);
    if (!slug) {
      setGuide(null);
      setGuideErr(null);
      return;
    }

    // slug 不合法：不要请求后端（会必然 404），直接提示用户修正
    if (!isValidGuideSlug(slug)) {
      setGuide(null);
      setGuideLoading(false);
      setGuideErr('该任务绑定的 guideSlug 不合法（仅支持小写字母/数字/-）。请编辑任务，把 slug 改成如：game-research / reading。');
      return;
    }

    // 命中缓存：直接展示，省掉 loading（并且避免重复请求）
    const cached = guideCacheRef.current.get(slug);
    if (cached) {
      setGuide(cached);
      setGuideErr(null);
      setGuideLoading(false);
      return;
    }

    // 已经加载过原则列表，但列表里根本不存在该 slug：不要再请求后端（避免刷 404），直接提示一次即可。
    if (allGuides.length > 0 && !allGuides.some((g) => g.slug === slug)) {
      const msg = `该任务绑定的原则已不存在：${slug}（可能你刚刚重置了系统示例原则）。请编辑该任务重新绑定。`;
      setGuide(null);
      setGuideLoading(false);
      setGuideErr(msg);
      if (!missingGuideNotifiedRef.current.has(slug)) {
        missingGuideNotifiedRef.current.add(slug);
        setToast(msg);
        if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
        toastTimerRef.current = window.setTimeout(() => setToast(null), 5200);
      }
      return;
    }

    let cancelled = false;
    setGuideLoading(true);
    setGuideErr(null);

    const load = async () => {
      try {
        // 系统内置：走 task-guides
        if (slug.startsWith('sys-')) {
          const res = await api.get<TaskGuide>(`/task-guides/slug/${encodeURIComponent(slug)}`);
          if (cancelled) return;
          cachePut(slug, res.data);
          setGuide(res.data);
          return;
        }
        if (isLoggedIn) {
          const res = await api.get<TaskGuide>(`/user-guides/slug/${encodeURIComponent(slug)}`);
          if (cancelled) return;
          cachePut(slug, res.data);
          setGuide(res.data);
          return;
        }
        const local = safeJsonParse<TaskGuide[]>(localStorage.getItem(LOCAL_GUIDES_KEY)) ?? [];
        const found = local.find((g) => g.slug === slug) ?? null;
        if (!found) {
          const msg = `该任务绑定的原则已不存在：${slug}。请编辑该任务重新绑定。`;
          setGuide(null);
          setGuideErr(msg);
          if (!missingGuideNotifiedRef.current.has(slug)) {
            missingGuideNotifiedRef.current.add(slug);
            setToast(msg);
            if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
            toastTimerRef.current = window.setTimeout(() => setToast(null), 5200);
          }
          return;
        }
        cachePut(slug, found);
        setGuide(found);
        return;
      } catch (e: any) {
        const status = e?.response?.status;
        if (isLoggedIn && !slug.startsWith('sys-') && (status === 404 || status === 405)) {
          try {
            const list = await api.get<TaskGuide[]>('/user-guides', { params: { q: slug } });
            if (cancelled) return;
            const exact = list.data.find((g) => g.slug === slug) ?? list.data[0] ?? null;
            if (!exact) {
              const msg = `该任务绑定的原则已不存在：${slug}。请编辑该任务重新绑定。`;
              setGuide(null);
              setGuideErr(msg);
              if (!missingGuideNotifiedRef.current.has(slug)) {
                missingGuideNotifiedRef.current.add(slug);
                setToast(msg);
                if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
                toastTimerRef.current = window.setTimeout(() => setToast(null), 5200);
              }
              return;
            }
            cachePut(slug, exact);
            setGuide(exact);
            return;
          } catch {
            if (cancelled) return;
            setGuide(null);
            setGuideErr('加载任务原则失败。');
            return;
          }
        }

        if (cancelled) return;
        setGuide(null);
        if (status === 404) {
          const msg = `该任务绑定的原则已不存在：${slug}。请编辑该任务重新绑定。`;
          setGuideErr(msg);
          if (!missingGuideNotifiedRef.current.has(slug)) {
            missingGuideNotifiedRef.current.add(slug);
            setToast(msg);
            if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
            toastTimerRef.current = window.setTimeout(() => setToast(null), 5200);
          }
        } else {
          setGuideErr('加载任务原则失败。');
        }
      } finally {
        if (cancelled) return;
        setGuideLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [selectedItem?.guideSlug, isLoggedIn]);

  const guideHtml = useMemo(() => {
    if (!guide?.content) return '';
    return marked.parse(guide.content) as string;
  }, [guide?.content]);

  return (
    <Container>
      {toast ? <Toast>{toast}</Toast> : null}
      <Header>
        <div>
          <Title>Today</Title>
          <SubTitle>
            全局清单一份；完成状态按「凌晨 {DAY_CUTOFF_HOUR}:00」切天（{dayKey}）。
          </SubTitle>
        </div>
        <HeaderRight>
          <Pill>完成：{completionText}</Pill>
          <InlineAuth>
            {authUser ? (
              <>
                <AuthName>
                  已登录：<strong>{authUser.username}</strong>（云端）
                  {cloudUpdatedAt ? <span style={{ marginLeft: 6 }}>云端：{new Date(cloudUpdatedAt).toLocaleString()}</span> : null}
                </AuthName>
                <Button onClick={pullCloud} title="从云端拉取并覆盖本地（建议先导出备份）" disabled={cloudLoading}>
                  拉取云端
                </Button>
                <Button onClick={() => void pushCloud(true)} disabled={cloudLoading} title="强制本地覆盖云端（忽略冲突检测）">
                  覆盖云端
                </Button>
                <Button onClick={logout}>退出</Button>
              </>
            ) : (
              <>
                <AuthName>当前：{guestOk || !isLoggedIn ? '游客（本地）' : '未登录'}</AuthName>
              </>
            )}
          </InlineAuth>
          <Button onClick={resetDone} title="清空今天的完成状态（不影响清单内容）">重置今日完成</Button>
          <Button onClick={exportJson} title="导出当前清单与最近记录（用于备份/跨设备迁移）">导出</Button>
          <Button onClick={() => fileInputRef.current?.click()} title="从 JSON 文件导入（会覆盖本地数据）">导入</Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              void importJson(f);
              e.currentTarget.value = '';
            }}
          />
        </HeaderRight>
      </Header>

      {cloudErr ? <Banner>{cloudErr}</Banner> : null}

      <Grid>
        <Left>
          <Row>
            <Input
              placeholder="新增一条（回车添加）"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') addItem();
              }}
            />
            <Button onClick={addItem}>添加</Button>
          </Row>

          <List>
            {items.length === 0 ? (
              <Empty>先加几条清单项吧（比如：训练 / 学习 / 写作 / 复盘）。</Empty>
            ) : (
              (() => {
                const renderNode = (it: ChecklistItem, level: number, idx: number, siblingCount: number) => {
                  const done = isDerivedDone(it);
                  const active = it.id === selectedId;
                  const isEditing = editingId === it.id;
                  const children = getChildren(it.id);
                  const open = expanded[it.id] ?? true;

                  return (
                    <div key={it.id} style={{ marginBottom: '0.45rem' }}>
                      <ItemRow
                        $active={active}
                        onClick={() => {
                          setSelectedId(it.id);
                        }}
                        style={{ paddingLeft: `${0.75 + level * 1.15}rem` }}
                      >
                        <LeftControls>
                          <Checkbox
                            type="checkbox"
                            checked={done}
                            onChange={() => toggleDone(it.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="done"
                          />
                        </LeftControls>
                        <ItemContent>
                          {isEditing ? (
                            <div style={{ display: 'grid', gap: '0.45rem' }}>
                              <SmallInput
                                value={editTitle}
                                autoFocus
                                onFocus={(e) => e.currentTarget.select()}
                                onChange={(e) => setEditTitle(e.target.value)}
                              />
                              <SmallInput
                                placeholder="可选：绑定原则 slug（比如：fitness / painting）"
                                list="today-guide-slug-suggestions"
                                value={editGuideSlug}
                                onChange={(e) => {
                                  setEditGuideSlug(e.target.value);
                                  setEditSlugErr(null);
                                }}
                              />
                              {(() => {
                                const raw = normalizeGuideSlug(editGuideSlug);
                                const suggested = raw ? suggestSlugSmart(raw) : '';
                                const showSuggest = !!raw && !isValidGuideSlug(raw) && !!suggested;
                                return (
                                  <>
                                    {editSlugErr ? <SlugHintRow>{editSlugErr}</SlugHintRow> : null}
                                    {showSuggest ? (
                                      <SlugHintRow>
                                        <span>
                                          检测到非标准 slug，建议：<strong>{suggested}</strong>
                                        </span>
                                        <SlugHintBtn onClick={() => setEditGuideSlug(suggested)}>应用建议</SlugHintBtn>
                                      </SlugHintRow>
                                    ) : raw && !isValidGuideSlug(raw) ? (
                                      <SlugHintRow>
                                        <span>slug 仅支持小写字母/数字/-（例如：game-research / reading）。</span>
                                      </SlugHintRow>
                                    ) : null}
                                  </>
                                );
                              })()}
                              <datalist id="today-guide-slug-suggestions">
                                {allGuides.map((g) => (
                                  <option key={g.id} value={g.slug} />
                                ))}
                              </datalist>
                            </div>
                          ) : (
                            <>
                              <InlineHeader>
                                <ItemTitle $done={done}>{it.title}</ItemTitle>
                                <InlineMeta title={it.guideSlug ?? undefined}>
                                  {it.guideSlug ? `原则：${it.guideSlug}` : '未绑定原则'}
                                </InlineMeta>
                              </InlineHeader>
                            </>
                          )}

                          <ItemActions onClick={(e) => e.stopPropagation()} style={{ marginTop: '0.45rem' }}>
                            <ActionBtn
                              onClick={() => moveItem(it.id, 'up')}
                              disabled={idx === 0}
                              title="上移（同级）"
                            >
                              ↑
                            </ActionBtn>
                            <ActionBtn
                              onClick={() => moveItem(it.id, 'down')}
                              disabled={idx === siblingCount - 1}
                              title="下移（同级）"
                            >
                              ↓
                            </ActionBtn>

                            <ActionBtn
                              onClick={() => {
                                addChild(it.id);
                              }}
                              title="添加子任务"
                            >
                              +子
                            </ActionBtn>

                            {isEditing ? (
                              <>
                                <ActionBtn onClick={saveEdit} title="保存">保存</ActionBtn>
                                <ActionBtn onClick={cancelEdit} title="取消">取消</ActionBtn>
                              </>
                            ) : (
                              <ActionBtn onClick={() => startEdit(it)} title="编辑标题/绑定原则">编辑</ActionBtn>
                            )}

                            <ActionBtn onClick={() => removeItem(it.id)} title="删除（含子任务）">删除</ActionBtn>
                          </ItemActions>
                        </ItemContent>
                        <ExpandCell onClick={(e) => e.stopPropagation()}>
                          {children.length ? (
                            <ActionBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(it.id);
                              }}
                              title={open ? '收起子任务' : '展开子任务'}
                              style={{ padding: '0.15rem 0.35rem' }}
                            >
                              {open ? '▾' : '▸'}
                            </ActionBtn>
                          ) : null}
                        </ExpandCell>
                      </ItemRow>

                      {children.length && open ? (
                        <div style={{ marginTop: '0.35rem' }}>
                          {children.map((c, cIdx) => renderNode(c, level + 1, cIdx, children.length))}
                        </div>
                      ) : null}
                    </div>
                  );
                };

                const roots = childrenMap.get(null) ?? [];
                return roots.map((r, rIdx) => renderNode(r, 0, rIdx, roots.length));
              })()
            )}
          </List>
        </Left>

        <Right>
          {!selectedItem ? (
            <Empty>请选择一条清单项（左侧）。</Empty>
          ) : (
            <>
              <RightTop>
                <div>
                  <RightTitle>原则回顾</RightTitle>
                  <RightHint>
                    当前项：<strong>{selectedItem.title}</strong>
                    {selectedItem.guideSlug ? (
                      <>
                        {' '}（{selectedItem.guideSlug}）
                      </>
                    ) : null}
                  </RightHint>
                </div>

                {selectedItem.guideSlug ? (
                  <RightLink to={`/guides/${normalizeGuideSlug(selectedItem.guideSlug)}`}>打开完整原则</RightLink>
                ) : (
                  <RightLink to="/guides">去看看已有原则</RightLink>
                )}
              </RightTop>

              <PomodoroCard>
                <PomodoroTop>
                  <div style={{ minWidth: 240 }}>
                    <PomodoroTime>{formatMMSS(pomodoro.remainingSec)}</PomodoroTime>
                    <PomodoroMeta>
                      绑定：{selectedItem.title} · 模式：
                      {pomodoro.mode === 'focus'
                        ? `专注 ${pomodoro.effective.focusMinutes}m`
                        : `休息 ${pomodoro.effective.breakMinutes}m`}
                      · 今日番茄：{pomodoro.todayPomodoros}
                    </PomodoroMeta>
                  </div>
                  <PomodoroActions>
                    <TinyBtn
                      $primary
                      onClick={() => {
                        if (!selectedItem) return;
                        const next = !pomodoro.running;
                        if (next && pomodoro.settings.autoFullscreen) {
                          void enterFullscreen();
                          pomodoro.setFocusScreen(true);
                        }
                        pomodoro.toggleRunning();
                      }}
                      disabled={!selectedItem}
                      title="开始/暂停"
                    >
                      {pomodoro.running ? '暂停' : '开始'}
                    </TinyBtn>
                    <TinyBtn onClick={() => pomodoro.reset(pomodoro.mode)} title="重置当前模式">重置</TinyBtn>
                    <TinyBtn
                      onClick={() => {
                        pomodoro.completeOrSkipBreak();
                      }}
                      disabled={!selectedItem}
                      title={pomodoro.mode === 'focus' ? '本次专注直接记为完成（+1 番茄）' : '提前结束休息，回到专注'}
                    >
                      {pomodoro.mode === 'focus' ? '完成' : '结束休息'}
                    </TinyBtn>
                    <TinyBtn
                      onClick={() => {
                        void enterFullscreen();
                        pomodoro.setFocusScreen(true);
                      }}
                      title="进入专注屏（最大化显示）"
                    >
                      专注屏
                    </TinyBtn>
                    <TinyBtn onClick={() => pomodoro.setShowSettings(true)} title="设置">
                      设置
                    </TinyBtn>
                  </PomodoroActions>
                </PomodoroTop>
              </PomodoroCard>

              {!selectedItem.guideSlug ? (
                <Empty>
                  这条清单项还没绑定任务原则。点左侧「编辑」，填一个 `guideSlug`（比如 `fitness`），右侧就会自动显示对应原则内容。
                </Empty>
              ) : guideLoading && !guide ? (
                <Empty>加载中...</Empty>
              ) : guideErr ? (
                <Empty>{guideErr}</Empty>
              ) : !guide ? (
                <Empty>暂无内容。</Empty>
              ) : (
                <>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <h2 style={{ margin: 0 }}>{guide.title}</h2>
                    {guide.description ? (
                      <div style={{ color: '#666', marginTop: '0.35rem' }}>{guide.description}</div>
                    ) : null}
                    {guideLoading ? (
                      <div style={{ color: '#666', marginTop: '0.35rem' }}>后台加载中…</div>
                    ) : null}
                  </div>
                  <Markdown dangerouslySetInnerHTML={{ __html: guideHtml }} />
                </>
              )}
            </>
          )}
        </Right>
      </Grid>

      {pomodoro.showSettings ? (
        <ModalOverlay onClick={() => pomodoro.setShowSettings(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 900, marginBottom: '0.25rem' }}>番茄时钟设置</div>
            <div style={{ color: 'inherit', opacity: 0.85, lineHeight: 1.6 }}>
              默认 25/5；切换任务会停止计时。背景/音效先提供默认占位，后续可扩展。
            </div>

            <ModalField>
              <div>专注时长（分钟）</div>
              <ModalInput
                type="number"
                min={1}
                max={180}
                value={pomodoro.settings.focusMinutes}
                onChange={(e) =>
                  pomodoro.setSettings((s) => ({ ...s, focusMinutes: clamp(Number(e.target.value || 0), 1, 180) }))
                }
              />
            </ModalField>

            <ModalField>
              <div>休息时长（分钟）</div>
              <ModalInput
                type="number"
                min={1}
                max={60}
                value={pomodoro.settings.breakMinutes}
                onChange={(e) =>
                  pomodoro.setSettings((s) => ({ ...s, breakMinutes: clamp(Number(e.target.value || 0), 1, 60) }))
                }
              />
            </ModalField>

            <ModalField>
              <div>
                <input
                  type="checkbox"
                  checked={pomodoro.settings.autoFullscreen}
                  onChange={(e) => pomodoro.setSettings((s) => ({ ...s, autoFullscreen: e.target.checked }))}
                />{' '}
                开始专注时自动进入专注屏
              </div>
            </ModalField>

            <ModalField>
              <div>
                <input
                  type="checkbox"
                  checked={pomodoro.settings.soundEnabled}
                  onChange={(e) => pomodoro.setSettings((s) => ({ ...s, soundEnabled: e.target.checked }))}
                />{' '}
                背景音效（默认占位，后续接入具体音源）
              </div>
            </ModalField>

            <ModalField>
              <div>音量（0~1）</div>
              <ModalInput
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={pomodoro.settings.volume}
                onChange={(e) =>
                  pomodoro.setSettings((s) => ({ ...s, volume: clamp(Number(e.target.value || 0), 0, 1) }))
                }
              />
            </ModalField>

            {selectedItem ? (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(0,0,0,0.08)' }}>
                <div style={{ fontWeight: 900, marginBottom: '0.35rem' }}>当前任务/原则偏好（可选）</div>
                <div style={{ opacity: 0.78, lineHeight: 1.6, marginBottom: '0.55rem' }}>
                  你可以为某个任务（taskId）或某条原则（guideSlug）设置更适合的番茄时长。优先级：任务 &gt; 原则 &gt;
                  全局默认。
                </div>

                {/* taskId override */}
                {(() => {
                  const tid = selectedItem.id;
                  const cur = pomodoro.settings.profiles.byTaskId[tid];
                  const enabled = !!cur;
                  return (
                    <div style={{ marginBottom: '0.65rem' }}>
                      <div style={{ fontWeight: 800, marginBottom: '0.25rem' }}>任务：{selectedItem.title}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => {
                              const on = e.target.checked;
                              pomodoro.setSettings((s) => {
                                const next = { ...s, profiles: { ...s.profiles, byTaskId: { ...s.profiles.byTaskId } } };
                                if (!on) {
                                  delete next.profiles.byTaskId[tid];
                                  return next;
                                }
                                next.profiles.byTaskId[tid] = {
                                  focusMinutes: s.focusMinutes,
                                  breakMinutes: s.breakMinutes,
                                  disabled: false,
                                };
                                return next;
                              });
                            }}
                          />
                          为该任务启用独立时长
                        </label>

                        {enabled ? (
                          <>
                            <span style={{ opacity: 0.75 }}>专注</span>
                            <ModalInput
                              style={{ width: 84 }}
                              type="number"
                              min={1}
                              max={180}
                              value={cur?.focusMinutes ?? pomodoro.settings.focusMinutes}
                              onChange={(e) =>
                                pomodoro.setSettings((s) => {
                                  const next = { ...s, profiles: { ...s.profiles, byTaskId: { ...s.profiles.byTaskId } } };
                                  const base = next.profiles.byTaskId[tid] ?? {
                                    focusMinutes: s.focusMinutes,
                                    breakMinutes: s.breakMinutes,
                                    disabled: false,
                                  };
                                  next.profiles.byTaskId[tid] = {
                                    ...base,
                                    focusMinutes: clamp(Number(e.target.value || 0), 1, 180),
                                  };
                                  return next;
                                })
                              }
                            />
                            <span style={{ opacity: 0.75 }}>休息</span>
                            <ModalInput
                              style={{ width: 84 }}
                              type="number"
                              min={1}
                              max={60}
                              value={cur?.breakMinutes ?? pomodoro.settings.breakMinutes}
                              onChange={(e) =>
                                pomodoro.setSettings((s) => {
                                  const next = { ...s, profiles: { ...s.profiles, byTaskId: { ...s.profiles.byTaskId } } };
                                  const base = next.profiles.byTaskId[tid] ?? {
                                    focusMinutes: s.focusMinutes,
                                    breakMinutes: s.breakMinutes,
                                    disabled: false,
                                  };
                                  next.profiles.byTaskId[tid] = {
                                    ...base,
                                    breakMinutes: clamp(Number(e.target.value || 0), 1, 60),
                                  };
                                  return next;
                                })
                              }
                            />
                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                              <input
                                type="checkbox"
                                checked={!!cur?.disabled}
                                onChange={(e) =>
                                  pomodoro.setSettings((s) => {
                                    const next = { ...s, profiles: { ...s.profiles, byTaskId: { ...s.profiles.byTaskId } } };
                                    const base = next.profiles.byTaskId[tid];
                                    if (!base) return next;
                                    next.profiles.byTaskId[tid] = { ...base, disabled: e.target.checked };
                                    return next;
                                  })
                                }
                              />
                              不启用番茄
                            </label>
                            <ModalBtn
                              onClick={() =>
                                pomodoro.setSettings((s) => {
                                  const next = { ...s, profiles: { ...s.profiles, byTaskId: { ...s.profiles.byTaskId } } };
                                  delete next.profiles.byTaskId[tid];
                                  return next;
                                })
                              }
                            >
                              移除任务偏好
                            </ModalBtn>
                          </>
                        ) : null}
                      </div>
                    </div>
                  );
                })()}

                {/* guideSlug override */}
                {selectedItem.guideSlug ? (
                  (() => {
                    const slug = normalizeGuideSlug(selectedItem.guideSlug ?? '');
                    if (!slug) return null;
                    const cur = pomodoro.settings.profiles.byGuideSlug[slug];
                    const enabled = !!cur;
                    return (
                      <div>
                        <div style={{ fontWeight: 800, marginBottom: '0.25rem' }}>原则：{slug}</div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={(e) => {
                                const on = e.target.checked;
                                pomodoro.setSettings((s) => {
                                  const next = { ...s, profiles: { ...s.profiles, byGuideSlug: { ...s.profiles.byGuideSlug } } };
                                  if (!on) {
                                    delete next.profiles.byGuideSlug[slug];
                                    return next;
                                  }
                                  next.profiles.byGuideSlug[slug] = {
                                    focusMinutes: s.focusMinutes,
                                    breakMinutes: s.breakMinutes,
                                    disabled: false,
                                  };
                                  return next;
                                });
                              }}
                            />
                            为该原则启用独立时长
                          </label>

                          {enabled ? (
                            <>
                              <span style={{ opacity: 0.75 }}>专注</span>
                              <ModalInput
                                style={{ width: 84 }}
                                type="number"
                                min={1}
                                max={180}
                                value={cur?.focusMinutes ?? pomodoro.settings.focusMinutes}
                                onChange={(e) =>
                                  pomodoro.setSettings((s) => {
                                    const next = { ...s, profiles: { ...s.profiles, byGuideSlug: { ...s.profiles.byGuideSlug } } };
                                    const base = next.profiles.byGuideSlug[slug] ?? {
                                      focusMinutes: s.focusMinutes,
                                      breakMinutes: s.breakMinutes,
                                      disabled: false,
                                    };
                                    next.profiles.byGuideSlug[slug] = {
                                      ...base,
                                      focusMinutes: clamp(Number(e.target.value || 0), 1, 180),
                                    };
                                    return next;
                                  })
                                }
                              />
                              <span style={{ opacity: 0.75 }}>休息</span>
                              <ModalInput
                                style={{ width: 84 }}
                                type="number"
                                min={1}
                                max={60}
                                value={cur?.breakMinutes ?? pomodoro.settings.breakMinutes}
                                onChange={(e) =>
                                  pomodoro.setSettings((s) => {
                                    const next = { ...s, profiles: { ...s.profiles, byGuideSlug: { ...s.profiles.byGuideSlug } } };
                                    const base = next.profiles.byGuideSlug[slug] ?? {
                                      focusMinutes: s.focusMinutes,
                                      breakMinutes: s.breakMinutes,
                                      disabled: false,
                                    };
                                    next.profiles.byGuideSlug[slug] = {
                                      ...base,
                                      breakMinutes: clamp(Number(e.target.value || 0), 1, 60),
                                    };
                                    return next;
                                  })
                                }
                              />
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                                <input
                                  type="checkbox"
                                  checked={!!cur?.disabled}
                                  onChange={(e) =>
                                    pomodoro.setSettings((s) => {
                                      const next = { ...s, profiles: { ...s.profiles, byGuideSlug: { ...s.profiles.byGuideSlug } } };
                                      const base = next.profiles.byGuideSlug[slug];
                                      if (!base) return next;
                                      next.profiles.byGuideSlug[slug] = { ...base, disabled: e.target.checked };
                                      return next;
                                    })
                                  }
                                />
                                不启用番茄
                              </label>
                              <ModalBtn
                                onClick={() =>
                                  pomodoro.setSettings((s) => {
                                    const next = { ...s, profiles: { ...s.profiles, byGuideSlug: { ...s.profiles.byGuideSlug } } };
                                    delete next.profiles.byGuideSlug[slug];
                                    return next;
                                  })
                                }
                              >
                                移除原则偏好
                              </ModalBtn>
                            </>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()
                ) : null}
              </div>
            ) : null}

            <ModalActions>
              <ModalBtn onClick={() => pomodoro.setShowSettings(false)}>关闭</ModalBtn>
              <ModalBtn
                $primary
                onClick={() => {
                  // 保存已在 effect 中做了，这里顺便把当前模式重置为新时长（不打断运行则不重置）
                  if (!pomodoro.running) pomodoro.reset(pomodoro.mode);
                  pomodoro.setShowSettings(false);
                }}
              >
                保存
              </ModalBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}

      {pomodoro.focusScreen ? (
        <FocusOverlay
          ref={focusOverlayRef}
          onClick={() => {
            // 点击背景不退出，避免误触
          }}
        >
          <FocusContent>
            <FocusTitle>
              {pomodoro.mode === 'focus' ? '专注' : '休息'} · {selectedItem ? selectedItem.title : '未选择任务'}
            </FocusTitle>
            <FocusBigTime>{formatMMSS(pomodoro.remainingSec)}</FocusBigTime>
            <div style={{ opacity: 0.85, marginTop: '0.5rem' }}>
              今日番茄：{pomodoro.todayPomodoros} · {pomodoro.effective.focusMinutes}/{pomodoro.effective.breakMinutes}
            </div>

            <FocusButtons>
              <TinyBtn
                $primary
                onClick={() => {
                  if (!selectedItem) return;
                  pomodoro.toggleRunning();
                }}
                disabled={!selectedItem}
              >
                {pomodoro.running ? '暂停' : '开始'}
              </TinyBtn>
              <TinyBtn onClick={() => pomodoro.reset(pomodoro.mode)}>重置</TinyBtn>
              <TinyBtn
                onClick={() => {
                  if (!selectedItem) return;
                  pomodoro.completeOrSkipBreak();
                }}
                disabled={!selectedItem}
                title={pomodoro.mode === 'focus' ? '本次专注直接记为完成（+1 番茄）' : '提前结束休息，回到专注'}
              >
                {pomodoro.mode === 'focus' ? '完成' : '结束休息'}
              </TinyBtn>
              <TinyBtn
                onClick={() => pomodoro.setFocusShowGuide(!pomodoro.focusShowGuide)}
                title={selectedItem?.guideSlug ? '显示/隐藏该任务的原则' : '显示/隐藏提示（未绑定原则）'}
              >
                {pomodoro.focusShowGuide ? '隐藏原则' : '显示原则'}
              </TinyBtn>
              <TinyBtn
                onClick={async () => {
                  pomodoro.setFocusScreen(false);
                  await exitFullscreen();
                }}
              >
                退出专注屏
              </TinyBtn>
            </FocusButtons>

            {pomodoro.focusShowGuide ? (
              <FocusGuideWrap>
                <FocusGuideTitle>
                  {selectedItem?.guideSlug ? `原则：${normalizeGuideSlug(selectedItem.guideSlug)}` : '未绑定原则（你仍可以在这里做提醒）'}
                </FocusGuideTitle>
                <FocusGuideBody>
                  {selectedItem?.guideSlug ? (
                    guideLoading && !guide ? (
                      <div style={{ opacity: 0.7 }}>加载中…</div>
                    ) : guideErr ? (
                      <div style={{ opacity: 0.7 }}>{guideErr}</div>
                    ) : guide ? (
                      <div dangerouslySetInnerHTML={{ __html: guideHtml }} />
                    ) : (
                      <div style={{ opacity: 0.7 }}>暂无内容。</div>
                    )
                  ) : (
                    <div style={{ opacity: 0.7 }}>
                      这条任务还没有绑定原则。你可以退出专注屏后在左侧任务中点击「编辑」，绑定一个 `guideSlug`。
                    </div>
                  )}
                </FocusGuideBody>
              </FocusGuideWrap>
            ) : null}
          </FocusContent>
        </FocusOverlay>
      ) : null}

      {confirmParentId ? (
        <ModalOverlay onClick={() => setConfirmParentId(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <div style={{ fontWeight: 800, marginBottom: '0.5rem' }}>提示</div>
            <div style={{ lineHeight: 1.6 }}>
              该任务还有<strong>未完成的子任务</strong>。确定要将主任务标记为完成吗？
            </div>
            <ModalActions>
              <ModalBtn onClick={() => setConfirmParentId(null)}>取消</ModalBtn>
              <ModalBtn $primary onClick={confirmMarkParentDone}>
                确认
              </ModalBtn>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </Container>
  );
};

export default Today;

